import { WbethExchangeRateAbi } from '../abis/binance'
import { TotalSupplyAbi } from '../abis/shared'
import type { LstRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type LstReader } from './shared'

/**
 * Binance wBETH reader — `exchangeRate()`.
 *
 * Two calls: `wBETH.totalSupply()` + `wBETH.exchangeRate()`, both 1e18.
 * Structurally the same as the Swell `getRate()` reader, kept separate so
 * the fragment and its caveats travel with the protocol.
 *
 * The token is the *same address* on Ethereum and BNB
 * (`0xa2E3356610840701BDf5611a53974510Ae27E2e1`) behind two different
 * implementations (`WrapTokenV3ETH` / `WrapTokenV2BSC`), but the read
 * surface and the pushed rate are identical, so one reader serves both
 * chain entries. `totalSupply` is genuinely per-chain.
 *
 * NOTE on sizing: ~57 % of the Ethereum supply sits in Binance's own hot
 * wallet, so `totalAssets` here is the accounting total, not the DeFi
 * float.
 */
export const readerBinanceWbeth = (entry: LstRegistryEntry): LstReader => ({
  calls: [
    { address: entry.address, name: 'totalSupply', params: [] },
    { address: entry.address, name: 'exchangeRate', params: [] },
  ],
  abis: [TotalSupplyAbi, WbethExchangeRateAbi],
  parse: ([supply, rate]) => {
    const totalSupply = toBigInt(supply)
    const exchangeRate = toBigInt(rate)
    if (totalSupply === undefined || exchangeRate === undefined) {
      return undefined
    }
    return {
      totalAssets: (totalSupply * exchangeRate) / ONE_E18,
      totalSupply,
      exchangeRate,
    }
  },
})
